import { useEffect, useReducer, useState } from "react";
import { toast } from "sonner";
import { useViewAsClient } from "@/lib/viewAsClient";
import { listBookings, listBookingLifecycles, listSitesLite } from "@/lib/api";

export const ALL = "__all__"; // sentinel so we don't pass empty strings to <Select />

const INITIAL_FILTERS = {
  search: "", siteId: ALL, status: ALL, direction: ALL,
  dateFrom: "", dateTo: "", incompleteOnly: false, onHoldOnly: false,
};

const orNull = (v) => (v === ALL || v === "" ? null : v);

function useLifecycles(rows) {
  const [lifecycle, dispatch] = useReducer((_s, action) => action.payload, new Map());
  useEffect(() => {
    if (!rows || rows.length === 0) { dispatch({ payload: new Map() }); return undefined; }
    let cancelled = false;
    listBookingLifecycles(rows.map((r) => r.id)).then((m) => { if (!cancelled) dispatch({ payload: m }); });
    return () => { cancelled = true; };
  }, [rows]);
  return lifecycle;
}

// Filter state + booking rows + site list + lifecycle badges for the Bookings page.
export function useBookingsData() {
  const { viewAsClientId } = useViewAsClient();
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((k) => k + 1);
  const setFilter = (key) => (value) =>
    setFilters((f) => ({ ...f, [key]: typeof value === "function" ? value(f[key]) : value }));

  useEffect(() => {
    let cancelled = false;
    listSitesLite(viewAsClientId || null).then(({ data }) => { if (!cancelled) setSites(data || []); });
    return () => { cancelled = true; };
  }, [viewAsClientId]);

  useEffect(() => {
    let cancelled = false;
    const f = filters;
    listBookings({
      search: f.search,
      siteId: orNull(f.siteId),
      status: orNull(f.status),
      direction: orNull(f.direction),
      dateFrom: orNull(f.dateFrom),
      dateTo: orNull(f.dateTo),
      clientId: viewAsClientId || null,
      incompleteOnly: f.incompleteOnly,
      onHold: f.onHoldOnly ? true : null,
    }).then(({ data, error }) => {
      if (cancelled) return;
      if (error) toast.error(error.message);
      setRows(data || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [filters, viewAsClientId, tick]);

  const lifecycle = useLifecycles(rows);
  return { filters, setFilter, rows, sites, loading, refresh, lifecycle };
}

// Multi-select; drops ids that are no longer visible so the count stays honest.
export function useSelection(rows) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(rows.map((r) => r.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);
  const toggleOne = (id) => setSelectedIds((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelectedIds((s) => (s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  const clear = () => setSelectedIds(new Set());
  return { selectedIds, toggleOne, toggleAll, clear };
}
